<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import WidgetPreviewHost from './components/WidgetPreviewHost.vue';
import {
  getWidgetLocaleMessages,
  readWidgetLocaleText,
} from 'dashboard/helper/widgetLocale';

const SUPPORTED_LANGUAGES = [
  { label: 'English', value: 'en' },
  { label: 'Bahasa Malaysia', value: 'ms' },
  { label: 'Bahasa Indonesia', value: 'id' },
  { label: 'Chinese (Simplified)', value: 'zh_CN' },
  { label: 'Chinese (Traditional)', value: 'zh_TW' },
  { label: 'Arabic', value: 'ar' },
  { label: 'French', value: 'fr' },
  { label: 'German', value: 'de' },
  { label: 'Hindi', value: 'hi' },
  { label: 'Italian', value: 'it' },
  { label: 'Japanese', value: 'ja' },
  { label: 'Korean', value: 'ko' },
  { label: 'Portuguese', value: 'pt' },
  { label: 'Russian', value: 'ru' },
  { label: 'Spanish', value: 'es' },
  { label: 'Thai', value: 'th' },
  { label: 'Turkish', value: 'tr' },
  { label: 'Vietnamese', value: 'vi' },
  { label: 'Polish', value: 'pl' },
  { label: 'Dutch', value: 'nl' },
  { label: 'Swedish', value: 'sv' },
  { label: 'Norwegian', value: 'no' },
  { label: 'Danish', value: 'da' },
  { label: 'Finnish', value: 'fi' },
  { label: 'Czech', value: 'cs' },
  { label: 'Romanian', value: 'ro' },
  { label: 'Hungarian', value: 'hu' },
  { label: 'Ukrainian', value: 'uk' },
  { label: 'Greek', value: 'el' },
];

const LEGACY_DEFAULT_CONTACT_CARD_MESSAGE =
  "Hello! We're away right now. Leave us a message and we'll get back to you.";
const LEGACY_DEFAULT_TICKET_CONFIRMATION_MESSAGE =
  "Thank you! Your message has been received. We'll get back to you as soon as possible.";
const LEGACY_DEFAULT_QUEUED_CUSTOMER_INFO =
  'One of our agents will be with you shortly. You are number %number% in the queue. Estimated wait time: %minutes% minutes. Thank you for your patience.';
const LEGACY_DEFAULT_RICH_GREETING_MESSAGE =
  'Hi, let us know if you have any questions.';
const LEGACY_DEFAULT_RICH_GREETING_PRIMARY_BUTTON = 'Chat now';
const LEGACY_DEFAULT_RICH_GREETING_SECONDARY_BUTTON = 'Just browsing';
const LEGACY_DEFAULT_CHAT_CLOSED_BY_CUSTOMER_NOTICE = 'You have closed the chat.';
const LEGACY_DEFAULT_CHAT_CLOSED_BY_INACTIVITY_NOTICE =
  'Chat ended automatically due to your inactivity. Feel free to start it again.';
const LEGACY_DEFAULT_CUSTOMER_NAME = 'Customer';
const LEGACY_DEFAULT_MESSAGE_PLACEHOLDER = 'Type your message...';
const LEGACY_DEFAULT_HOME_TAB_LABEL = 'Home';
const LEGACY_DEFAULT_CHAT_TAB_LABEL = 'Chat';

export default {
  name: 'LanguageSettings',
  components: { NextButton, WidgetPreviewHost },
  props: {
    inbox: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      expandedSection: 'welcome',
      langPickerOpen: false,
      langSearchQuery: '',
      widgetLanguage: 'en',
      welcomeMessage: '',
      defaultCustomerName: '',
      messagePlaceholder: '',
      homeTabLabel: '',
      chatTabLabel: '',
      contactCardMessage: '',
      ticketConfirmationMessage: '',
      queuedCustomerInfo: '',
      richGreetingMessage: '',
      richGreetingPrimaryButton: '',
      richGreetingSecondaryButton: '',
      chatClosedByCustomerNotice: '',
      chatClosedByInactivityNotice: '',
      isSaving: false,
      previewDocHtml: '',
    };
  },
  computed: {
    ...mapGetters({ uiFlags: 'inboxes/getUIFlags' }),
    widgetLocaleMessages() {
      return getWidgetLocaleMessages(this.widgetLanguage || this.inbox.widget_language);
    },
    languageSearchPlaceholder() {
      return readWidgetLocaleText(
        this.widgetLocaleMessages,
        'EMOJI.PLACEHOLDER',
        'Search...'
      );
    },
    languageSearchEmptyText() {
      return readWidgetLocaleText(
        this.widgetLocaleMessages,
        'PRE_CHAT_FORM.FIELDS.PHONE_NUMBER.DROPDOWN_EMPTY',
        'No results'
      );
    },
    welcomeMessagePlaceholder() {
      return readWidgetLocaleText(
        this.widgetLocaleMessages,
        'START_NEW_CONVERSATION',
        'Hello! How can we help you today?'
      );
    },
    defaultCustomerNamePlaceholder() {
      return readWidgetLocaleText(
        this.widgetLocaleMessages,
        'YOU',
        LEGACY_DEFAULT_CUSTOMER_NAME
      );
    },
    chatMessagePlaceholderText() {
      return readWidgetLocaleText(
        this.widgetLocaleMessages,
        'CHAT_PLACEHOLDER',
        LEGACY_DEFAULT_MESSAGE_PLACEHOLDER
      );
    },
    filteredLanguages() {
      const q = this.langSearchQuery.toLowerCase();
      if (!q) return SUPPORTED_LANGUAGES;
      return SUPPORTED_LANGUAGES.filter(l => l.label.toLowerCase().includes(q));
    },
    selectedLanguageLabel() {
      const lang = SUPPORTED_LANGUAGES.find(
        l => l.value === this.widgetLanguage
      );
      return lang ? lang.label : 'English';
    },
    previewScene() {
      if (this.expandedSection === 'away') return 'away';
      if (this.expandedSection === 'queue') return 'queue';
      return 'home';
    },
    previewSceneData() {
      if (this.previewScene !== 'queue') return {};
      const replyTime = this.inbox.reply_time || 'in_a_few_minutes';
      const minutesMap = {
        in_a_few_minutes: 3,
        in_a_few_hours: 60,
        in_a_day: 720,
      };
      return {
        isQueued: true,
        queuePosition: 1,
        estimatedMinutes: minutesMap[replyTime] || 3,
      };
    },
    livePreviewBaseUrl() {
      const script = this.inbox.web_widget_script || '';
      const match = script.match(/var\s+BASE_URL\s*=\s*['"]([^'"]+)['"]/);
      return match?.[1] || window.location.origin;
    },
    livePreviewWebsiteToken() {
      return this.inbox.website_token || '';
    },
    resolvedPreviewDarkMode() {
      const themeConfig = this.inbox.widget_theme_config || {};
      const isMidnight = this.inbox.widget_theme_preset === 'midnight';
      if (isMidnight) {
        if (themeConfig?.midnightVariant === 'light') return 'light';
        if (themeConfig?.midnightVariant === 'dark') return 'dark';
        if (themeConfig?.darkMode === 'light') return 'light';
        return 'dark';
      }
      if (['light', 'dark'].includes(themeConfig?.darkMode)) {
        return themeConfig.darkMode;
      }
      return 'light';
    },
    liveChatwootSettings() {
      const existingThemeCfg = this.inbox.widget_theme_config || {};
      const languagePhrases = this.buildLanguagePhrasesPayload();
      return {
        locale: this.widgetLanguage,
        widget_language: this.widgetLanguage,
        position: this.inbox.position || 'right',
        type: 'standard',
        allowThemeOverride: true,
        darkMode: this.resolvedPreviewDarkMode,
        themePreset: this.inbox.widget_theme_preset || 'classic',
        widgetStyle: 'rounded',
        widgetColor: this.inbox.widget_color || '#0059e1',
        iconColor: '#ffffff',
        themeConfig: {
          ...(existingThemeCfg || {}),
          languagePhrases,
        },
        welcomeTitle: this.inbox.welcome_title || '',
        welcomeDescription: this.inbox.welcome_tagline || '',
        announcementText: this.inbox.announcement_text || '',
        announcementUrl: this.inbox.announcement_url || '',
        announcementBgColor: this.inbox.announcement_bg_color || '#242529',
        announcementTextColor: this.inbox.announcement_text_color || '#ffffff',
        hideMessageBubble: false,
        enableFileUpload: true,
        enableEmojiPicker: true,
        enableEndConversation: true,
        initialRoute: this.previewScene === 'queue' ? 'messages' : 'home',
        previewScene: this.previewScene,
        previewSceneData: this.previewSceneData,
      };
    },
  },
  watch: {
    inbox() {
      this.loadFromInbox();
      this.previewDocHtml = this.buildPreviewDoc();
    },
    widgetLanguage() { this.pushSettingsToPreview(); },
    welcomeMessage() { this.pushSettingsToPreview(); },
    defaultCustomerName() { this.pushSettingsToPreview(); },
    messagePlaceholder() { this.pushSettingsToPreview(); },
    contactCardMessage() { this.pushSettingsToPreview(); },
    ticketConfirmationMessage() { this.pushSettingsToPreview(); },
    queuedCustomerInfo() { this.pushSettingsToPreview(); },
    '$route.query.languageSection'() { this.handleRouteDrivenSection(false); },
  },
  mounted() {
    this.loadFromInbox();
    this.previewDocHtml = this.buildPreviewDoc();
    document.addEventListener('click', this.handleOutsideClick);
    this.handleRouteDrivenSection(true);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleOutsideClick);
  },
  methods: {
    normalizePhraseValue(value) {
      return String(value || '').trim();
    },
    resolveOptionalPhrase(value, legacyDefault = '') {
      const normalizedValue = this.normalizePhraseValue(value);
      const normalizedLegacyDefault = this.normalizePhraseValue(legacyDefault);
      if (!normalizedValue) return '';
      if (normalizedLegacyDefault && normalizedValue === normalizedLegacyDefault) {
        return '';
      }
      return normalizedValue;
    },
    includePhraseOverride(phrases, key, value, legacyDefault = '') {
      const resolved = this.resolveOptionalPhrase(value, legacyDefault);
      if (!resolved) return;
      phrases[key] = resolved;
    },
    buildLanguagePhrasesPayload() {
      const languagePhrases = {};
      this.includePhraseOverride(languagePhrases, 'welcomeMessage', this.welcomeMessage);
      this.includePhraseOverride(
        languagePhrases,
        'defaultCustomerName',
        this.defaultCustomerName,
        LEGACY_DEFAULT_CUSTOMER_NAME
      );
      this.includePhraseOverride(
        languagePhrases,
        'messagePlaceholder',
        this.messagePlaceholder,
        LEGACY_DEFAULT_MESSAGE_PLACEHOLDER
      );
      this.includePhraseOverride(
        languagePhrases,
        'homeTabLabel',
        this.homeTabLabel,
        LEGACY_DEFAULT_HOME_TAB_LABEL
      );
      this.includePhraseOverride(
        languagePhrases,
        'chatTabLabel',
        this.chatTabLabel,
        LEGACY_DEFAULT_CHAT_TAB_LABEL
      );
      this.includePhraseOverride(
        languagePhrases,
        'contactCardMessage',
        this.contactCardMessage,
        LEGACY_DEFAULT_CONTACT_CARD_MESSAGE
      );
      this.includePhraseOverride(
        languagePhrases,
        'ticketConfirmationMessage',
        this.ticketConfirmationMessage,
        LEGACY_DEFAULT_TICKET_CONFIRMATION_MESSAGE
      );
      this.includePhraseOverride(
        languagePhrases,
        'queuedCustomerInfo',
        this.queuedCustomerInfo,
        LEGACY_DEFAULT_QUEUED_CUSTOMER_INFO
      );
      this.includePhraseOverride(
        languagePhrases,
        'richGreetingMessage',
        this.richGreetingMessage,
        LEGACY_DEFAULT_RICH_GREETING_MESSAGE
      );
      this.includePhraseOverride(
        languagePhrases,
        'richGreetingPrimaryButton',
        this.richGreetingPrimaryButton,
        LEGACY_DEFAULT_RICH_GREETING_PRIMARY_BUTTON
      );
      this.includePhraseOverride(
        languagePhrases,
        'richGreetingSecondaryButton',
        this.richGreetingSecondaryButton,
        LEGACY_DEFAULT_RICH_GREETING_SECONDARY_BUTTON
      );
      this.includePhraseOverride(
        languagePhrases,
        'chatClosedByCustomerNotice',
        this.chatClosedByCustomerNotice,
        LEGACY_DEFAULT_CHAT_CLOSED_BY_CUSTOMER_NOTICE
      );
      this.includePhraseOverride(
        languagePhrases,
        'chatClosedByInactivityNotice',
        this.chatClosedByInactivityNotice,
        LEGACY_DEFAULT_CHAT_CLOSED_BY_INACTIVITY_NOTICE
      );
      return languagePhrases;
    },
    loadFromInbox() {
      const themeCfg = this.inbox.widget_theme_config || {};
      const cfg = themeCfg.languagePhrases || {};
      this.widgetLanguage = this.inbox.widget_language || 'en';
      this.welcomeMessage = this.resolveOptionalPhrase(
        cfg.welcomeMessage || this.inbox.greeting_message || ''
      );
      this.defaultCustomerName = this.resolveOptionalPhrase(
        cfg.defaultCustomerName,
        LEGACY_DEFAULT_CUSTOMER_NAME
      );
      this.messagePlaceholder = this.resolveOptionalPhrase(
        cfg.messagePlaceholder,
        LEGACY_DEFAULT_MESSAGE_PLACEHOLDER
      );
      this.homeTabLabel = this.resolveOptionalPhrase(
        cfg.homeTabLabel,
        LEGACY_DEFAULT_HOME_TAB_LABEL
      );
      this.chatTabLabel = this.resolveOptionalPhrase(
        cfg.chatTabLabel,
        LEGACY_DEFAULT_CHAT_TAB_LABEL
      );
      this.contactCardMessage = this.resolveOptionalPhrase(
        cfg.contactCardMessage,
        LEGACY_DEFAULT_CONTACT_CARD_MESSAGE
      );
      this.ticketConfirmationMessage = this.resolveOptionalPhrase(
        cfg.ticketConfirmationMessage,
        LEGACY_DEFAULT_TICKET_CONFIRMATION_MESSAGE
      );
      this.queuedCustomerInfo = this.resolveOptionalPhrase(
        cfg.queuedCustomerInfo,
        LEGACY_DEFAULT_QUEUED_CUSTOMER_INFO
      );
      this.richGreetingMessage = this.resolveOptionalPhrase(
        cfg.richGreetingMessage,
        LEGACY_DEFAULT_RICH_GREETING_MESSAGE
      );
      this.richGreetingPrimaryButton = this.resolveOptionalPhrase(
        cfg.richGreetingPrimaryButton,
        LEGACY_DEFAULT_RICH_GREETING_PRIMARY_BUTTON
      );
      this.richGreetingSecondaryButton = this.resolveOptionalPhrase(
        cfg.richGreetingSecondaryButton,
        LEGACY_DEFAULT_RICH_GREETING_SECONDARY_BUTTON
      );
      this.chatClosedByCustomerNotice = this.resolveOptionalPhrase(
        cfg.chatClosedByCustomerNotice,
        LEGACY_DEFAULT_CHAT_CLOSED_BY_CUSTOMER_NOTICE
      );
      this.chatClosedByInactivityNotice = this.resolveOptionalPhrase(
        cfg.chatClosedByInactivityNotice,
        LEGACY_DEFAULT_CHAT_CLOSED_BY_INACTIVITY_NOTICE
      );
    },
    toggleSection(key) {
      this.expandedSection = this.expandedSection === key ? null : key;
    },
    handleRouteDrivenSection(isInitial = false) {
      const target = String(this.$route.query?.languageSection || '').trim();
      if (!['away', 'queue', 'welcome'].includes(target)) return;
      if (isInitial && this.expandedSection !== target) {
        this.expandedSection = target;
        return;
      }
      this.expandedSection = null;
      this.$nextTick(() => {
        window.requestAnimationFrame(() => {
          this.expandedSection = target;
        });
      });
    },
    toggleLangPicker() {
      this.langPickerOpen = !this.langPickerOpen;
      if (this.langPickerOpen) {
        this.langSearchQuery = '';
        this.$nextTick(() => this.$refs.langSearch?.focus());
      }
    },
    selectLanguage(lang) {
      this.widgetLanguage = lang.value;
      this.langPickerOpen = false;
      this.langSearchQuery = '';
    },
    handleOutsideClick(e) {
      if (
        this.langPickerOpen &&
        this.$refs.langPicker &&
        !this.$refs.langPicker.contains(e.target)
      ) {
        this.langPickerOpen = false;
      }
    },
    buildPreviewDoc() {
      if (!this.livePreviewWebsiteToken) return '';
      const isMidnight = this.inbox.widget_theme_preset === 'midnight';
      const bg =
        isMidnight && this.inbox.widget_theme_config?.background
          ? this.inbox.widget_theme_config.background
          : isMidnight
            ? '#111111'
            : '#ffffff';
      const settingsJSON = JSON.stringify(this.liveChatwootSettings)
        .split('<')
        .join('\\u003c');
      const baseUrl = JSON.stringify(this.livePreviewBaseUrl);
      const token = JSON.stringify(this.livePreviewWebsiteToken);
      const SO = '\x3cscript\x3e';
      const SC = '\x3c\x2fscript\x3e';
      return (
        `<!doctype html><html><head>` +
        `<meta charset="utf-8"/>` +
        `<meta name="viewport" content="width=device-width,initial-scale=1"/>` +
        `<style>html,body{margin:0;width:100%;height:100%;background:${bg};overflow:hidden;}` +
        `body{position:fixed;inset:0;overflow:hidden;}` +
        `.woot--bubble-holder{visibility:hidden;transition:visibility 0s;}</style>` +
        `</head><body>` +
        SO +
        `window.wlabSettings=${settingsJSON};` +
        `window.addEventListener('message',function(e){` +
        `try{var d;if(typeof e.data==='string')d=JSON.parse(e.data);else d=e.data;` +
        `if(d&&d.type==='wlab-settings'){` +
        `var f=document.getElementById('chatwoot_live_chat_widget');if(!f)return;` +
        `f.contentWindow.postMessage('chatwoot-widget:'+JSON.stringify(` +
        `Object.assign({event:'config-set'},d.s,{locale:(d.s&&d.s.locale)||'en',showUnreadMessagesDialog:true,isPreviewUpdate:true})),'*');` +
        `if(d.s.darkMode)f.contentWindow.postMessage('chatwoot-widget:'+` +
        `JSON.stringify({event:'set-color-scheme',darkMode:d.s.darkMode}),'*');` +
        `}}catch(x){}});` +
        `(function(d,t){var B=${baseUrl},T=${token};` +
        `var g=d.createElement(t),s=d.getElementsByTagName(t)[0];` +
        `g.src=B+"/packs/js/sdk.js?v="+Date.now();g.async=true;` +
        `s.parentNode.insertBefore(g,s);` +
        `g.onload=function(){window.wlabSDK.run({websiteToken:T,baseUrl:B});` +
        `setTimeout(function(){if(window.$chatwoot)window.$chatwoot.toggle('open');},800);` +
        `setTimeout(function(){setInterval(function(){` +
        `var wh=document.querySelector('.woot-widget-holder');` +
        `var bh=document.querySelector('.woot--bubble-holder');` +
        `if(!wh||!bh)return;` +
        `bh.style.visibility=wh.classList.contains('woot--hide')?'visible':'hidden';` +
        `},250);},1600);};` +
        `})(document,"script");` +
        SC +
        `</body></html>`
      );
    },
    pushSettingsToPreview() {
      const iframe = this.$refs.sdkFrame;
      if (!iframe || !iframe.contentWindow) return;
      try {
        iframe.contentWindow.postMessage(
          JSON.stringify({
            type: 'wlab-settings',
            s: this.liveChatwootSettings,
          }),
          '*'
        );
      } catch (e) {
        // ignore
      }
    },
    openWebsitePreview() {
      const url =
        this.inbox.website_url || this.inbox.channel?.website_url || '';
      if (!url) return;
      window.open(url, '_blank', 'noopener,noreferrer');
    },
    async save() {
      this.isSaving = true;
      try {
        const existingThemeCfg = this.inbox.widget_theme_config || {};
        const languagePhrases = this.buildLanguagePhrasesPayload();
        await this.$store.dispatch('inboxes/updateInbox', {
          id: this.inbox.id,
          greeting_message: this.welcomeMessage,
          channel: {
            widget_language: this.widgetLanguage,
            widget_theme_config: {
              ...existingThemeCfg,
              languagePhrases,
            },
          },
        });
        useAlert('Language settings saved');
      } catch (e) {
        useAlert(e.message || 'Unable to save language settings');
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<template>
  <!-- Root: full two-panel layout, no lc-sidebar -->
  <div class="ls-page">
    <div class="ls-layout">
<!-- ═══════════════════════════════════════════════
           LEFT PANEL: Settings
           ═══════════════════════════════════════════════ -->
      <div class="ls-settings-panel">
        <!-- Top header bar (56px, white, border-bottom) -->
        <div class="ls-header-bar">
          <div class="ls-header-title">Language</div>
        </div>

        <!-- Scrollable content area -->
        <div id="settings-overflow-wrapper" class="ls-content-scroll">
          <div class="ls-content-inner">
            <form @submit.prevent>
<!-- ─── Select language ─── -->
              <div class="ls-field-label">Select language</div>
              <div ref="langPicker" class="ls-lang-picker-wrap">
                <!-- Trigger -->
                <button
                  type="button"
                  class="ls-picker-trigger"
                  :aria-expanded="langPickerOpen"
                  aria-haspopup="listbox"
                  role="combobox"
                  @click.stop="toggleLangPicker"
                >
                  <div class="ls-picker-trigger__content">
                    <div class="ls-picker-trigger__body">
                      <div class="ls-picker-trigger__item">
                        <div class="ls-picker-trigger__item-text">
                          {{ selectedLanguageLabel }}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="ls-picker-trigger__controls">
                    <span
                      class="ls-chevron-icon"
                      :class="{ open: langPickerOpen }"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        width="20"
                        height="20"
                      >
                        <path
                          stroke="currentcolor"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="m6 9 6 6 6-6"
                        />
                      </svg>
                    </span>
                  </div>
                </button>

                <!-- Dropdown list -->
                <div v-if="langPickerOpen" class="ls-picker-dropdown">
                  <div class="ls-picker-search-wrap">
                    <input
                      ref="langSearch"
                      v-model="langSearchQuery"
                      type="text"
                      :placeholder="languageSearchPlaceholder"
                      class="ls-picker-search"
                      @click.stop
                    />
                  </div>
                  <ul class="ls-picker-list" role="listbox">
                    <li
                      v-for="lang in filteredLanguages"
                      :key="lang.value"
                      role="option"
                      class="ls-picker-option"
                      :class="{ selected: lang.value === widgetLanguage }"
                      @click.stop="selectLanguage(lang)"
                    >
                      {{ lang.label }}
                    </li>
                    <li
                      v-if="filteredLanguages.length === 0"
                      class="ls-picker-empty"
                    >
                      {{ languageSearchEmptyText }}
                    </li>
                  </ul>
                </div>
              </div>

              <!-- ─── Customize phrases ─── -->
              <div class="ls-field-label">Customize phrases</div>
              <div class="ls-accordions">
<!-- 1. Welcome your customers -->
                <div class="ls-accordion" tabindex="0">
                  <div
                    class="ls-accordion__header"
                    :aria-expanded="expandedSection === 'welcome'"
                    @click="toggleSection('welcome')"
                  >
                    <span class="ls-accordion__trigger-inner">
                      <span class="ls-accordion__icon">
                        <!-- Hand / wave icon -->
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          width="20"
                          height="20"
                        >
                          <path
                            fill="currentcolor"
                            d="M14.53 2.224a2.5 2.5 0 0 0-2.124-.398 2.5 2.5 0 0 0-1.518 1.164L8.332 7.417a2.87 2.87 0 0 0-2.717-1.822 2.5 2.5 0 0 0-2.459 2.472c-.042 3.807-.05 6.053-.017 6.667l.02.363v.001a7 7 0 0 0 3.203 5.478q.05.037.106.07l1.732 1a7 7 0 0 0 9.562-2.562l4.25-7.361a2.5 2.5 0 0 0-.706-3.28l-2.873 4.977a.5.5 0 0 1-.866-.5l3.176-5.5A2.5 2.5 0 0 0 19.1 4.89l-3.565 6.175a.5.5 0 1 1-.866-.5l4.25-7.362a.5.5 0 0 1 .066-.09 2.5 2.5 0 0 0-2.937-1.53 1 1 0 0 1-.035.073l-4.25 7.361a.5.5 0 1 1-.866-.5z"
                          />
                        </svg>
                      </span>
                      <span class="ls-accordion__text">
                        <span class="ls-accordion__title">Welcome your customers</span>
                      </span>
                      <span class="ls-accordion__chevron">
                        <!-- down chevron when expanded, right chevron when collapsed — exactly as LiveChat -->
                        <svg
                          v-if="expandedSection === 'welcome'"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          width="20"
                          height="20"
                        >
                          <path
                            stroke="currentcolor"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="m6 9 6 6 6-6"
                          />
                        </svg>
                        <svg
                          v-else
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          width="20"
                          height="20"
                        >
                          <path
                            stroke="currentcolor"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="m9 6 6 6-6 6"
                          />
                        </svg>
                      </span>
                    </span>
                  </div>
                  <div
                    class="ls-accordion__body"
                    :class="{ collapsed: expandedSection !== 'welcome' }"
                  >
                    <div class="ls-accordion__body-inner">
                      <div class="ls-fields-grid">
                        <div class="ls-field">
                          <label class="ls-label" for="field-welcome-msg">Welcome message</label>
                          <div class="ls-input-wrap">
                            <span class="ls-input-span">
                              <input
                                id="field-welcome-msg"
                                v-model="welcomeMessage"
                                type="text"
                                class="ls-input"
                                :placeholder="welcomeMessagePlaceholder"
                              />
                            </span>
                          </div>
                        </div>
                        <div class="ls-field">
                          <label class="ls-label" for="field-customer-name">Default customer name</label>
                          <div class="ls-input-wrap">
                            <span class="ls-input-span">
                              <input
                                id="field-customer-name"
                                v-model="defaultCustomerName"
                                type="text"
                                class="ls-input"
                                :placeholder="defaultCustomerNamePlaceholder"
                              />
                            </span>
                          </div>
                        </div>
                        <div class="ls-field">
                          <label class="ls-label" for="field-placeholder">Message placeholder</label>
                          <div class="ls-input-wrap">
                            <span class="ls-input-span">
                              <input
                                id="field-placeholder"
                                v-model="messagePlaceholder"
                                type="text"
                                class="ls-input"
                                :placeholder="chatMessagePlaceholderText"
                              />
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- 2. Agents are away -->
                <div class="ls-accordion" tabindex="0">
                  <div
                    class="ls-accordion__header"
                    :aria-expanded="expandedSection === 'away'"
                    @click="toggleSection('away')"
                  >
                    <span class="ls-accordion__trigger-inner">
                      <span class="ls-accordion__icon">
                        <!-- Person icon -->
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          width="20"
                          height="20"
                        >
                          <path
                            fill="currentcolor"
                            fill-rule="evenodd"
                            d="M12 2a5 5 0 1 0 0 10 5 5 0 0 0 0-10m-2 12a5 5 0 0 0-5 5v2a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2a5 5 0 0 0-5-5z"
                            clip-rule="evenodd"
                          />
                        </svg>
                      </span>
                      <span class="ls-accordion__text">
                        <span class="ls-accordion__title">Agents are away</span>
                        <span class="ls-accordion__subtitle">
                          Customers will see a ticket form. You can enable
                          asynchronous chats in
                          <a class="ls-inline-link" href="#">availability section</a>.
                        </span>
                      </span>
                      <span class="ls-accordion__chevron">
                        <svg
                          v-if="expandedSection === 'away'"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          width="20"
                          height="20"
                        >
                          <path
                            stroke="currentcolor"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="m6 9 6 6 6-6"
                          />
                        </svg>
                        <svg
                          v-else
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          width="20"
                          height="20"
                        >
                          <path
                            stroke="currentcolor"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="m9 6 6 6-6 6"
                          />
                        </svg>
                      </span>
                    </span>
                  </div>
                  <div
                    class="ls-accordion__body"
                    :class="{ collapsed: expandedSection !== 'away' }"
                  >
                    <div class="ls-accordion__body-inner">
                      <div class="ls-fields-grid">
                        <div class="ls-field">
                          <label class="ls-label" for="field-contact-card">Contact card message</label>
                          <div class="ls-input-wrap">
                            <span class="ls-input-span">
                              <input
                                id="field-contact-card"
                                v-model="contactCardMessage"
                                type="text"
                                class="ls-input"
                              />
                            </span>
                          </div>
                        </div>
                        <div class="ls-field">
                          <label class="ls-label" for="field-ticket-confirm">Ticket confirmation message</label>
                          <div class="ls-input-wrap">
                            <span class="ls-input-span">
                              <input
                                id="field-ticket-confirm"
                                v-model="ticketConfirmationMessage"
                                type="text"
                                class="ls-input"
                              />
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- 3. Customers get in a queue -->
                <div class="ls-accordion" tabindex="0">
                  <div
                    class="ls-accordion__header"
                    :aria-expanded="expandedSection === 'queue'"
                    @click="toggleSection('queue')"
                  >
                    <span class="ls-accordion__trigger-inner">
                      <span class="ls-accordion__icon">
                        <!-- Hourglass / queue icon -->
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          width="20"
                          height="20"
                        >
                          <path
                            fill="currentcolor"
                            fill-rule="evenodd"
                            d="M7 2a2 2 0 0 0-2 2v2a7 7 0 0 0 3.394 6A7 7 0 0 0 5 18v2a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-2a7 7 0 0 0-3.394-6A7 7 0 0 0 19 6V4a2 2 0 0 0-2-2zm0 4h10V4H7zm5 7a5 5 0 0 0-4.583 3h9.166A5 5 0 0 0 12 13"
                            clip-rule="evenodd"
                          />
                        </svg>
                      </span>
                      <span class="ls-accordion__text">
                        <span class="ls-accordion__title">Customers get in a queue</span>
                        <span class="ls-accordion__subtitle">
                          If all agents hit their limit or chat routing is set
                          to manual new customers will be queued.
                        </span>
                      </span>
                      <span class="ls-accordion__chevron">
                        <svg
                          v-if="expandedSection === 'queue'"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          width="20"
                          height="20"
                        >
                          <path
                            stroke="currentcolor"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="m6 9 6 6 6-6"
                          />
                        </svg>
                        <svg
                          v-else
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          width="20"
                          height="20"
                        >
                          <path
                            stroke="currentcolor"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="m9 6 6 6-6 6"
                          />
                        </svg>
                      </span>
                    </span>
                  </div>
                  <div
                    class="ls-accordion__body"
                    :class="{ collapsed: expandedSection !== 'queue' }"
                  >
                    <div class="ls-accordion__body-inner">
                      <div class="ls-fields-grid">
                        <div class="ls-field ls-field--full">
                          <label class="ls-label" for="field-queue-info">Queued customer info</label>
                          <div class="ls-input-wrap">
                            <div class="ls-textarea-wrap">
                              <div class="ls-slate-wrap">
                                <textarea
                                  id="field-queue-info"
                                  v-model="queuedCustomerInfo"
                                  class="ls-textarea"
                                  rows="4"
                                />
                              </div>
                            </div>
                          </div>
                          <div class="ls-variable-hint">
                            <span class="ls-hint-icon">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                width="16"
                                height="16"
                              >
                                <path
                                  stroke="currentcolor"
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                  stroke-width="2"
                                  d="M12 8h.01M11 12h1v4h1M3 12a9 9 0 1 0 18.001 0A9 9 0 0 0 3 12"
                                />
                              </svg>
                            </span>
                            <p>
                              Use variable <strong>%number%</strong> to tell
                              customers which place they occupy in the queue.
                              Use <strong>%minutes%</strong> to show when an
                              agent will reply.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div><!-- /ls-accordions -->

              <!-- Hidden submit trigger -->
              <button type="submit" class="ls-hidden-submit" />
            </form>
          </div>
          <!-- /ls-content-inner -->
        </div>
        <!-- /ls-content-scroll -->

        <!-- Sticky save bar -->
        <div class="ls-save-bar">
          <NextButton
            class="ls-save-btn"
            type="button"
            label="Save settings"
            :is-loading="isSaving"
            :disabled="isSaving"
            @click="save"
          />
        </div>
</div><!-- /ls-settings-panel -->

      <!-- ═══════════════════════════════════════════════
           RIGHT PANEL: Preview
           ═══════════════════════════════════════════════ -->
      <div class="ls-preview-panel">
        <!-- Preview header bar (gray, 56px) -->
        <div class="ls-preview-header-bar">
          <div class="ls-preview-title">Preview</div>
        </div>

        <!-- Preview content area (gray bg, scrollable) -->
        <div class="ls-preview-content-area">
          <div class="ls-preview-scroll-inner">
            <div class="ls-preview-widget-holder">
              <div class="ls-preview-widget-stage">
                <div class="ls-preview-widget-inner">
                  <!-- Scaled iframe — matches LiveChat's scale approach -->
                  <WidgetPreviewHost
                    :inbox="inbox"
                    :settings="liveChatwootSettings"
                    iframe-class="ls-preview-iframe"
                    empty-class="ls-preview-empty"
                  />
                </div>
              </div>
            </div>
            <p class="ls-preview-test-note">
              <a href="#" @click.prevent="openWebsitePreview">Test it out</a> on
              the chat page
            </p>
          </div>
        </div>
</div><!-- /ls-preview-panel -->
</div><!-- /ls-layout -->
  </div>
  <!-- /ls-page -->
</template>

<style scoped>
/* ══════════════════════════════════════════════════════
   Root & layout
   ══════════════════════════════════════════════════════ */
.ls-page {
  /* Use flex growth so the flex chain from Settings.vue propagates correctly */
  flex: 1;
  min-height: 0;
  overflow: hidden;
  background: #fff;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

.ls-layout {
  display: flex;
  align-items: stretch;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

/* ══════════════════════════════════════════════════════
   LEFT PANEL
   ══════════════════════════════════════════════════════ */
.ls-settings-panel {
  flex: 5 1 62.5%;
  min-width: 296px;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #fff;
  color: #111827;
  border-color: #eeeeef;
  box-sizing: border-box;
  position: relative;
}

/* Header bar */
.ls-header-bar {
  display: flex;
  width: 100%;
  height: 56px;
  position: relative;
  box-sizing: border-box;
  align-items: center;
  background: #fff;
  border-bottom: 1px solid #eeeeef;
  flex-shrink: 0;
}

.ls-header-title {
  height: 56px;
  line-height: 56px;
  letter-spacing: 0.3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-left: 21px;
  color: #111827;
  font-weight: 600;
  font-size: 18px;
  text-align: left;
}

/* Scrollable content */
.ls-content-scroll {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  z-index: 0;
  min-height: 0;
}

.ls-content-inner {
  padding: 32px 32px 120px;
  box-sizing: border-box;
  max-width: 960px;
}

/* Section labels (bold heading above picker / accordions) */
.ls-field-label {
  line-height: 24px;
  color: #111827;
  font-weight: 700;
  font-size: 16px;
  margin-bottom: 0;
}

/* ══════════════════════════════════════════════════════
   Language Picker
   ══════════════════════════════════════════════════════ */
.ls-lang-picker-wrap {
  position: relative;
  max-width: 300px;
  margin: 15px 0 40px;
}

.ls-picker-trigger {
  box-sizing: border-box;
  display: flex;
  position: relative;
  align-items: flex-start;
  justify-content: center;
  transition: border 0.15s ease-in-out;
  border: 1px solid #bcc6d0;
  border-radius: 8px;
  background: #fff;
  padding: 0 12px;
  width: 100%;
  line-height: 22px;
  color: #111827;
  font-size: 15px; /* LiveChat uses 15px */
  font-weight: 400;
  cursor: pointer;
  text-align: left;
}

.ls-picker-trigger:hover {
  border-color: #9ba8b3;
}

.ls-picker-trigger__content {
  display: flex;
  flex-grow: 2;
  align-items: center;
  overflow: hidden;
  height: 34px; /* LiveChat medium = 34px */
}

.ls-picker-trigger__body {
  display: flex;
  flex-wrap: nowrap;
  gap: 4px;
  justify-content: flex-start;
  max-width: 100%;
}

.ls-picker-trigger__item {
  display: flex;
  flex: 1;
  align-items: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ls-picker-trigger__item-text {
  text-wrap: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 14px;
  color: #111827;
}

.ls-picker-trigger__controls {
  display: flex;
  align-items: center;
  height: 34px; /* matches medium content height */
}

.ls-chevron-icon {
  display: flex;
  align-items: center;
  margin-left: 4px;
  color: #6b7280;
  transition: transform 0.2s ease;
}

.ls-chevron-icon.open {
  transform: rotate(180deg);
}

/* Dropdown */
.ls-picker-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid #eeeeef;
  border-radius: 8px;
  box-shadow: 0 6px 20px 0 rgba(19, 19, 23, 0.3);
  z-index: 100;
  overflow: hidden;
}

.ls-picker-search-wrap {
  padding: 8px;
  border-bottom: 1px solid #eeeeef;
}

.ls-picker-search {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #bcc6d0;
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 13px;
  color: #111827;
  background: #fff;
  outline: none;
}

.ls-picker-search:focus {
  border-color: #0059e1;
  box-shadow: 0 0 0 3px rgba(0, 89, 225, 0.15);
}

.ls-picker-list {
  list-style: none;
  margin: 0;
  padding: 4px 0;
  max-height: 240px;
  overflow-y: auto;
}

.ls-picker-option {
  padding: 8px 12px;
  font-size: 14px;
  color: #111827;
  cursor: pointer;
  transition: background 0.1s;
}

.ls-picker-option:hover {
  background: #f5f5f5;
}

.ls-picker-option.selected {
  background: rgba(0, 89, 225, 0.08);
  color: #0059e1;
  font-weight: 500;
}

.ls-picker-empty {
  padding: 12px;
  font-size: 13px;
  color: #9ca3af;
  text-align: center;
}

/* ══════════════════════════════════════════════════════
   Accordions
   ══════════════════════════════════════════════════════ */
.ls-accordions {
  margin: 15px 0 40px;
  font-size: 13px;
  color: #111827;
}

.ls-accordion {
  border: solid 1px #eeeeef;
  margin-bottom: 12px;
  border-radius: 4px;
  background: #fff;
  width: 100%;
  max-width: 705px;
  box-sizing: border-box;
  outline: none;
}

/* Accordion header */
.ls-accordion__header {
  min-height: 60px;
  font-size: 16px;
  cursor: pointer;
  color: #111827;
  font-weight: 600;
  display: flex;
  justify-content: space-between;
  align-items: center;
  overflow: hidden;
  border-color: transparent;
  padding: 0 17px;
  user-select: none;
}

.ls-accordion__header:hover {
  background: #f9fafb;
}

.ls-accordion__trigger-inner {
  display: inline-flex;
  align-items: center;
  padding: 17px 0;
  width: 100%;
  line-height: 24px;
}

.ls-accordion__icon {
  display: flex;
  justify-content: center;
  fill: #111827;
  align-self: flex-start;
  width: 18px;
  height: 24px;
  margin-right: 12px;
  flex-shrink: 0;
  color: #111827;
}

.ls-accordion__text {
  display: flex;
  flex-flow: column;
  width: 100%;
}

.ls-accordion__title {
  display: flex;
  width: 100%;
  align-items: center;
  font-size: 16px;
  font-weight: 600;
  color: #111827;
}

.ls-accordion__subtitle {
  max-width: 90%;
  font-size: 14px;
  line-height: 22px;
  font-weight: 400;
  padding-top: 2px;
  color: #6b7280;
}

.ls-inline-link {
  color: #0059e1;
  text-decoration: none;
}

.ls-inline-link:hover {
  text-decoration: underline;
}

.ls-accordion__chevron {
  fill: #111827;
  transition: transform 0.2s;
  margin-left: auto;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  color: #111827;
}

/* Accordion body (animated collapse) */
.ls-accordion__body {
  position: relative;
  transition:
    max-height 0.2s ease,
    opacity 0.2s ease;
  opacity: 1;
  max-height: 1200px;
}

.ls-accordion__body.collapsed {
  overflow: hidden;
  visibility: collapse;
  opacity: 0;
  z-index: -1;
  max-height: 0 !important;
}

.ls-accordion__body-inner {
  padding: 0 16px 16px;
}

/* Fields grid inside accordion */
.ls-fields-grid {
  display: flex;
  flex-wrap: wrap;
  margin-top: -16px;
  margin-right: -30px;
}

.ls-field {
  min-width: 200px;
  flex: 1 0 calc(50% - 30px);
  margin-top: 16px;
  margin-right: 30px;
  box-sizing: border-box;
}

.ls-field--full {
  flex: 1 0 calc(100% - 30px);
}

.ls-label {
  display: block;
  font-size: 13px;
  line-height: 20px;
  color: #111827;
  cursor: pointer; /* matches .css-1ic2u64 label { cursor: pointer } */
}

.ls-input-wrap {
  position: relative;
  width: 100%;
}

.ls-input-span {
  position: relative;
  display: block;
}

.ls-input {
  display: block;
  width: 100%;
  margin-top: 6px;
  font-size: 14px;
  padding: 11px 15px 10px;
  line-height: 22px;
  color: #111827;
  border: 1px solid #bcc6d0;
  border-radius: 4px;
  background: #fff;
  box-sizing: border-box;
  outline: none;
  transition: border 0.25s ease-in-out;
}

.ls-input:focus {
  border-color: #0059e1;
  box-shadow: 0 0 0 3px rgba(0, 89, 225, 0.12);
}

.ls-textarea-wrap {
  position: relative;
  width: 100%;
}

.ls-slate-wrap {
  position: relative;
  width: 100%;
  display: block;
}

.ls-textarea {
  display: block;
  width: 100%;
  margin-top: 6px;
  font-size: 14px;
  padding: 10px;
  line-height: 18px;
  color: #111827;
  border: 1px solid #bcc6d0;
  border-radius: 4px;
  background: #fff;
  box-sizing: border-box;
  outline: none;
  resize: vertical;
  min-height: 80px;
  transition: border 0.25s ease-in-out;
  font-family: inherit;
  overflow: hidden auto;
  overflow-wrap: break-word;
  white-space: pre-wrap;
}

.ls-textarea:focus {
  border-color: #0059e1;
  box-shadow: 0 0 0 3px rgba(0, 89, 225, 0.12);
}

/* Variable hint */
.ls-variable-hint {
  display: flex;
  align-items: flex-start;
  padding-top: 10px;
  opacity: 0.6;
  gap: 6px;
}

.ls-hint-icon {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  color: #374151;
}

.ls-variable-hint p {
  margin: 0;
  font-size: 13px;
  line-height: 20px;
  color: #374151;
}

.ls-variable-hint strong {
  font-weight: 600;
}

/* Hidden submit */
.ls-hidden-submit {
  display: none;
}

/* ══════════════════════════════════════════════════════
   Save bar (sticky bottom)
   ══════════════════════════════════════════════════════ */
.ls-save-bar {
  padding: 12px 32px;
  border-top: 1px solid #eeeeef;
  flex-shrink: 0;
  position: sticky;
  bottom: 0;
  z-index: 5;
  background: #fff;
}

.ls-save-bar :deep(.ls-save-btn) {
  height: 36px !important;
  padding: 0 16px !important;
  border-radius: 8px !important;
  background: #0059e1 !important;
  border: 1px solid #0059e1 !important;
  color: #fff !important;
  font-size: 14px !important;
  font-weight: 600 !important;
}

.ls-save-bar :deep(.ls-save-btn:hover) {
  background: #0047c2 !important;
  border-color: #0047c2 !important;
}

/* ══════════════════════════════════════════════════════
   RIGHT PANEL (Preview)
   ══════════════════════════════════════════════════════ */
.ls-preview-panel {
  box-sizing: border-box;
  height: 100%;
  border-left: 1px solid #eeeeef;
  min-width: 0;
  flex: 3 1 37.5%;
  z-index: 1; /* matches .css-17768wy z-index: 1 */
  transition: margin-left 0.2s ease-out;
  display: flex;
  flex-direction: column;
  background: #f5f5f5;
  color: #111827;
}

/* Preview header bar (same 56px height, gray bg) */
.ls-preview-header-bar {
  display: flex;
  width: 100%;
  height: 56px;
  position: relative;
  box-sizing: border-box;
  align-items: center;
  background: #f5f5f5;
  border-bottom: 1px solid #eeeeef;
  flex-shrink: 0;
}

.ls-preview-title {
  height: 56px;
  line-height: 56px;
  letter-spacing: 0.3px;
  color: #111827;
  font-weight: 600;
  font-size: 18px;
  text-align: left;
  margin-left: 20px;
}

/* Preview content area */
.ls-preview-content-area {
  position: relative;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  background: #f5f5f5;
  z-index: 0;
}

.ls-preview-scroll-inner {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
}

/* Widget holder: matches LiveChat's .css-ht54w1 */
.ls-preview-widget-holder {
  height: 620px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  position: relative;
  box-sizing: border-box;
  width: 360px;
  overflow: hidden;
  padding: 36px;
  margin: auto;
}

/* Inner stage: matches .css-1dyx40s */
.ls-preview-widget-stage {
  flex: 1 1 100%;
  width: 100%;
  height: 100%;
  position: relative;
  display: block;
  background: transparent;
  max-width: 320px;
}

/* Widget container: matches .css-1eyu34e */
.ls-preview-widget-inner {
  position: relative;
  flex: 1 1 0%;
  height: 100%;
}

/* Iframe scaled like LiveChat's widget — 360x682 scaled 0.8 → visual ~288x546 */
.ls-preview-iframe {
  width: 360px;
  height: 682px;
  border: none;
  display: block;
  border-radius: 16px;
  transform: scale(0.8);
  transform-origin: 0 0;
  box-shadow:
    rgba(0, 0, 0, 0.05) 0px 0.48px 2.41px -0.38px,
    rgba(0, 0, 0, 0.17) 0px 4px 20px -0.75px;
}

.ls-preview-empty {
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 12px;
  text-align: center;
}

/* "Test it out" link */
.ls-preview-test-note {
  font-size: 13px;
  color: #6b7280;
  text-align: center;
  margin: 8px 0 16px;
}

.ls-preview-test-note a {
  color: #0059e1;
  text-decoration: none;
}

.ls-preview-test-note a:hover {
  text-decoration: underline;
}
</style>
